import { ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { DEFAULT_OG_IMAGE, SITE_NAME, getCanonicalUrl } from "@/config/site";
import HomeNav from "@/pages/Home/HomeNav";
import HomeFooter from "@/pages/Home/HomeFooter";
import { SEOConfig } from '@/types/ui/public-page';

interface PublicPageTemplateProps {
  seo: SEOConfig;
  children: ReactNode;
  className?: string;
}

const PublicPageTemplate = ({ seo, children, className = "bg-[#F9FAFC]" }: PublicPageTemplateProps) => {
  const canonicalUrl = getCanonicalUrl(seo.canonicalUrl || window.location.pathname);
  const ogImage = seo.ogImage ? getCanonicalUrl(seo.ogImage) : DEFAULT_OG_IMAGE;
  const structuredData = seo.structuredData
    ? Array.isArray(seo.structuredData)
      ? seo.structuredData
      : [seo.structuredData]
    : [];

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        {seo.keywords && <meta name="keywords" content={seo.keywords.join(", ")} />}
        <meta property="og:title" content={seo.ogTitle || seo.title} />
        <meta property="og:description" content={seo.ogDescription || seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.ogTitle || seo.title} />
        <meta name="twitter:description" content={seo.ogDescription || seo.description} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="canonical" href={canonicalUrl} />
        {structuredData.map((entry, index) => (
          <script key={`structured-data-${index}`} type="application/ld+json">
            {JSON.stringify(entry)}
          </script>
        ))}
      </Helmet>

      <div className={`min-h-screen w-full flex flex-col ${className}`}>
        <HomeNav />
        <main className="flex-1 w-full">
          {children}
        </main>
        <HomeFooter />
      </div>
    </>
  );
};

export default PublicPageTemplate;
